import React, {
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import "./App.css";
import { Route, Switch } from "react-router-dom";
import Login from "./components/pages/Login/Login";
import Root from "./components/pages/Root/Root";
import enumStrings, { RedirectUri } from "./components/enumStrings";
import useInitialAuth from "./components/common/Hooks/useInitalAuth";
import ResponsiveDrawer from "./components/common/Navbar/Nav";
import { createStyles, makeStyles, Theme } from "@material-ui/core";
import { UserTypeContext } from "./UserTypeContext";
import { UserType } from "./graphql/types";
import RootAdmin from "./components/pages/Root/RootAdmin";
import ListAllChambers from "./components/pages/Root/ListAllChambers";
import CreateChamberAdmin from "./components/pages/Root/CreateChamber";
import getApolloClient from "./apolloClient";
import { ApolloClient, ApolloProvider } from "@apollo/client";
import RootChamberAdmin from "./components/pages/ChamberAdmin/RootChamberAdmin";
import ChamberAdminListAllChamberMembers from "./components/pages/ChamberAdmin/ChamberAdminListAllChambers";
import CreateChamberMember from "./components/pages/ChamberAdmin/CreateChamberMember";
import ChamberAdminImportAdmin from "./components/pages/ChamberAdmin/ChamberAdminImportMembers";
import ManageChamberMember from "./components/pages/ChamberAdmin/ManageAChamberMember";
import ManageChamberAsChamberAdmin from "./components/pages/ChamberAdmin/ChamberAdminManageChamber";
import GeneralError from "./components/common/ErrorPages/GeneralError";
import useRedirect from "./components/common/Hooks/useRedirect";
import ChamberMemberListDiscounts from "./components/pages/ChamberMemberAdmin/ListDiscount";
import ChamberMemberCreateDiscount from "./components/pages/ChamberMemberAdmin/CreateDiscount";
import ChamberMemberUpdateDiscount from "./components/pages/ChamberMemberAdmin/UpdateDiscount";
import ChamberMemberAdminSettings from "./components/pages/ChamberMemberAdmin/Settings";
import ForgotPassword from "./components/pages/Login/ForgotPassword";
import NewPassword from "./components/pages/Login/NewPassword";
import ChamberAdminListAllDiscounts from "./components/pages/ChamberAdmin/ListAllChamberDiscounts";
import CPAMarketingToolkit from "./components/pages/ChamberMemberAdmin/CPAMarketingToolkit";
// import General404 from "./components/common/ErrorPages/General404";
import CreatePerkPartner from "./components/pages/Root/CreatePerkPartner";
import ViewPerkPartners from "./components/pages/Root/ViewPerkPartners";
import EditPerkPartner from "./components/pages/Root/EditPerkPartner";
import DeleteCPAData from "./components/common/DeleteCPAData";
import CPACreatePerk from "./components/common/CPACreatePerk";
import { useHandleLogout } from "./components/common/Hooks/useHandleLogout";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    drawer: {
      "@media (min-width:1280px)": { marginLeft: "311px" },
      height: "100%",
      background:
        "linear-gradient(to bottom, #E1A731 18%, #E1A731 299px, #F5F5F5 -13%, #F5F5F5 79%)",
    },
  })
);

const LogoutManual = (): ReactElement => {
  const handleLogout = useHandleLogout();

  useEffect(() => {
    handleLogout();
    // only run on page load that's why ignoring
    // eslint-disable-next-line
  }, []);

  return <></>;
};

interface UserTypeRoutesType {
  [key: string]: RedirectUri;
}
const userTypeRoutes = {
  [RedirectUri.rootAdmin]: <RootAdmin />,
  [RedirectUri.listAllChamberAdmin]: <ListAllChambers />,
  [RedirectUri.createAChamber]: <CreateChamberAdmin />,
  [RedirectUri.updateAChamberRoute]: <ManageChamberAsChamberAdmin />,
  [RedirectUri.chamberAdmin]: <RootChamberAdmin />,
  [RedirectUri.manageAMemberChamberAdmin]: (
    <ChamberAdminListAllChamberMembers />
  ),
  [RedirectUri.createAMemberChamberAdmin]: <CreateChamberMember />,
  [RedirectUri.cpaMarketingToolkit]: <CPAMarketingToolkit />,
  [RedirectUri.settingsOfChamberChamberAdmin]: <ChamberMemberAdminSettings />,
  [RedirectUri.importAMemberChamberAdmin]: <ChamberAdminImportAdmin />,

  [RedirectUri.manageYourChamberChamberAdmin]: <ManageChamberAsChamberAdmin />,
  [RedirectUri.updateAMemberChamberAdmin]: <ManageChamberMember />,

  [RedirectUri.createDiscountChamberMemberAdmin]: (
    <ChamberMemberCreateDiscount />
  ),
  [RedirectUri.manageDiscountChamberMemberAdmin]: (
    <ChamberMemberListDiscounts />
  ),
  [RedirectUri.editDiscountChamberMemberAdmin]: <ChamberMemberUpdateDiscount />,
  [RedirectUri.settingsChamberMemberAdmin]: <ChamberMemberAdminSettings />,
  [RedirectUri.forgotPassword]: <ForgotPassword />,
  [RedirectUri.newPassword]: <NewPassword />,
  [RedirectUri.memberDiscountPageChamberAdmin]: (
    <ChamberAdminListAllDiscounts />
  ),
  [RedirectUri.editDiscountPageChamberAdmin]: <ChamberMemberUpdateDiscount />,
  [RedirectUri.memberDiscountCreatePageChamberAdmin]: (
    <ChamberMemberCreateDiscount />
  ),
  [RedirectUri.createAPerkPartner]: <CreatePerkPartner />,
  [RedirectUri.listAllPerkPartner]: <ViewPerkPartners />,
  [RedirectUri.editperkPartnerDiscountPageChamberAdmin]: <EditPerkPartner />,
  [RedirectUri.cpacreateperk]: <CPACreatePerk />,
} as unknown as UserTypeRoutesType;

const App = (): ReactElement => {
  const [client, setClient] = useState(
    null as unknown as ApolloClient<unknown>
  );
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useInitialAuth(setIsLoggedIn);
  const classes = useStyles();
  const {
    state: { userType },
  } = useContext(UserTypeContext);
  const redirectToError = useRedirect(RedirectUri.errorGeneral);

  // initialize graphql apollo client
  useEffect(() => {
    getApolloClient(redirectToError).then((newClient) => {
      setClient(newClient);
      setIsLoading(false);
    });
  }, [isLoggedIn, redirectToError]);

  const userRoutesToShow = useMemo(() => {
    if (!userType || userType.length === 0) {
      return [];
    }
    // @ts-ignore
    const routeObjs = Object.values(enumStrings.groups[userType as UserType]);
    return routeObjs.map((route: any, index) => (
      <Route exact path={route.route} key={index}>
        {userTypeRoutes[route.route]}
      </Route>
    ));
  }, [userType]);
  // create an array of routes based on user type

  if (isLoading || isLoggedIn === null) {
    return <div>{enumStrings.loadingText}</div>;
  }
  return (
    <ApolloProvider client={client}>
      <Switch>
        <Route exact path={RedirectUri.errorGeneral}>
          <GeneralError />
        </Route>
        <Route exact path={RedirectUri.home}>
          <Root />
        </Route>
        <Route exact path={RedirectUri.deleteCpaData}>
          <DeleteCPAData />
        </Route>
        <Route exact path={RedirectUri.newPassword}>
          <NewPassword />
        </Route>
        {isLoggedIn === false ? (
          <div>
            {isLoggedIn === false &&
            window.location.href !== RedirectUri.login ? (
              <Route exact path={RedirectUri.forgotPassword}>
                <ForgotPassword />
              </Route>
            ) : null}
            {isLoggedIn === false &&
            window.location.href !== RedirectUri.forgotPassword ? (
              <Route exact path={RedirectUri.login}>
                <Login />
              </Route>
            ) : null}
          </div>
        ) : (
          <div className={classes.drawer}>
            <ResponsiveDrawer />
            <Route exact path={RedirectUri.logout}>
              <LogoutManual />
            </Route>
            {userRoutesToShow}
            {/* <Route path="*">
              <General404 />
            </Route> */}
          </div>
        )}
      </Switch>
    </ApolloProvider>
  );
};

export default App;
